// PPS CRM - Leads Module (Round-Robin Distribution)
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';

interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  source: string;
  status: 'PENDING' | 'ASSIGNED' | 'CONVERTED' | 'REJECTED';
  assignedAgentId?: string | null;
  assignedAt?: string | null;
  createdAt: string;
}

interface AgentQueueItem {
  agentId: string;
  agentCode?: string;
  agentName: string;
  signedInAt: string;
  status: string;
}

export default function Leads() {
  const { user } = useAuthStore();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [queue, setQueue] = useState<AgentQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchQueue();
    fetchAgentStatus();
    const interval = setInterval(fetchAgentStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/api/leads');
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueue = async () => {
    if (user?.role === 'OWNER' || user?.role === 'MANAGER') {
      try {
        const response = await api.get('/api/leads/agent/queue');
        setQueue(response.data);
      } catch (error) {
        console.error('Error fetching agent queue:', error);
      }
    }
  };

  const fetchAgentStatus = async () => {
    if (user?.role !== 'AGENT') return;
    try {
      const response = await api.get('/api/leads/agent/status');
      setIsOnline(!!response.data.isOnline);
    } catch (error) {
      console.error('Error fetching agent status:', error);
    }
  };

  const handleSignIn = async () => {
    try {
      await api.post('/api/leads/agent/signin');
      setIsOnline(true);
      fetchQueue();
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await api.post('/api/leads/agent/signout');
      setIsOnline(false);
      fetchQueue();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleConvert = async (leadId: string) => {
    try {
      await api.patch(`/api/leads/${leadId}/convert`, { clientId: null });
      fetchLeads();
    } catch (error) {
      console.error('Error converting lead:', error);
    }
  };

  const handleReject = async (leadId: string) => {
    try {
      await api.patch(`/api/leads/${leadId}/reject`, { reason: 'Not a fit' });
      fetchLeads();
    } catch (error) {
      console.error('Error rejecting lead:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ASSIGNED':
        return 'bg-blue-100 text-blue-800';
      case 'CONVERTED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Leads</h2>

        <div className="flex items-center gap-4">
          {user?.role === 'AGENT' && (
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-sm">{isOnline ? 'Online' : 'Offline'}</span>
              {isOnline ? (
                <button
                  onClick={handleSignOut}
                  className="ml-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                >
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="ml-2 px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                >
                  Sign In
                </button>
              )}
            </div>
          )}

          <button
            onClick={() => setShowModal(true)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            + Add Lead
          </button>
        </div>
      </div>

      {(user?.role === 'OWNER' || user?.role === 'MANAGER') && queue.length > 0 && (
        <div className="card mb-6">
          <h3 className="font-semibold mb-3">Online Agents (Queue)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {queue.map((session, index) => (
              <div key={session.agentId} className="bg-light-gray p-3 rounded">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="font-medium">{session.agentName}</span>
                </div>
                <p className="text-sm text-dark-gray mt-1">
                  #{index + 1} in queue
                </p>
                <p className="text-xs text-mid-gray">{session.agentCode || '-'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-dark-gray">No leads found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-light-gray">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Source</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className="border-b border-light-gray hover:bg-light-gray">
                  <td className="px-4 py-3">
                    {lead.firstName} {lead.lastName}
                    <p className="text-sm text-dark-gray">{lead.email || lead.phone || '-'}</p>
                  </td>
                  <td className="px-4 py-3">{lead.source}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(lead.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleConvert(lead.id)}
                        className="text-green-600 hover:underline text-sm"
                      >
                        Convert
                      </button>
                      <button
                        onClick={() => handleReject(lead.id)}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <LeadModal
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchLeads();
          }}
        />
      )}
    </div>
  );
}

function LeadModal({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    source: 'marketing_site',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/leads', formData);
      onSave();
    } catch (error) {
      console.error('Error creating lead:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-xl font-bold mb-4">Add New Lead</h3>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Last Name *</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-mid-gray rounded"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Source</label>
            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-mid-gray rounded hover:bg-light-gray"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
